import axios from 'axios';
import appConfig from '@/app-config';
import { TokenStorage } from '@/_services/storage';

function authHeaders() {
  return {
    'x-tenant-id': TokenStorage.getTenant(),
    'Authorization': `Bearer ${TokenStorage.getToken()}`,
  };
}

export const CapacityHeatmapService = {
  /**
   * Fetch capacity data (available shift hours) for a date range
   * ProcessCalendarPreview does not expose BranchCode as a filterable field.
   * Fetch by date range only. Branch scoping is handled in buildUtilisationGrid.
   * @param {string} branchCode - Not used in filter (implicit via processes prop)
   * @param {string} dateStart - Start date (ISO format)
   * @param {string} dateEnd - End date (ISO format)
   * @returns {Promise<Array>} Array of capacity records with ProcessCode, ShiftDateStart, WorkHours
   */
  async getCapacityData(branchCode, dateStart, dateEnd) {
    const filter = `ShiftDateStart ge ${dateStart} and ShiftDateStart le ${dateEnd}`;
    const url = `${appConfig.VUE_APP_ROOT_API}/api/ProcessCalendarPreview?$filter=${filter}&$select=ProcessCode,ShiftDateStart,WorkHours,StandardDurationHours`;
    const response = await axios.get(url, { headers: authHeaders() });
    return response.data.value;
  },

  /**
   * Fetch demand data (scheduled work orders) for a branch and date range
   * BranchCode filter is supported ✅
   * Filter captures all WOs that OVERLAP with the date range (not just ScheduledDate)
   * This includes WOs that start before the range, end within it, or span the entire range
   * Hours are derived from EndDate − StartDate (PlannedHours field does not exist)
   * @param {string} branchCode - Branch code filter
   * @param {string} dateStart - Start date (ISO format)
   * @param {string} dateEnd - End date (ISO format)
   * @returns {Promise<Array>} Array of work order records
   */
  async getDemandData(branchCode, dateStart, dateEnd) {
    // Overlap filter: captures WOs where:
    // - StartDate falls within the range, OR
    // - EndDate falls within the range, OR
    // - WO completely spans the range (StartDate before, EndDate after)
    const filter = `(BranchCode eq '${branchCode}') and (FriendlyStatus ne 'Cancelled') and ((StartDate ge ${dateStart} and StartDate le ${dateEnd}) or (EndDate ge ${dateStart} and EndDate le ${dateEnd}) or (StartDate le ${dateStart} and EndDate ge ${dateEnd}))`;
    const url = `${appConfig.VUE_APP_ROOT_API}/api/ProductionScheduleWorkOrder?$filter=${filter}`;
    const response = await axios.get(url, { headers: authHeaders() });
    return response.data.value;
  },

  /**
   * Fetch feature settings for CapacityHeatmap (summary process list, export attribute, export value)
   * Settings are maintained by Graycorp Delivery/Support; no customer-facing edit UI
   * @returns {Promise<Object>} Settings object with keys: SummaryProcessList, ExportAttributeCode, ExportAttributeValue
   *   - Each key maps to the setting's SettingValue (or null if not found)
   *   - Caller must validate that required settings exist
   */
  async getFeatureSettings() {
    const filter = `ApplicationFeatureCode eq 'CapacityHeatmap'`;
    const select = `SettingName,SettingValue`;
    const url = `${appConfig.VUE_APP_ROOT_API}/api/ApplicationFeatureSetting?$filter=${filter}&$select=${select}`;
    const response = await axios.get(url, { headers: authHeaders() });

    const settingsMap = {};
    if (response.data.value) {
      response.data.value.forEach(setting => {
        const settingKey = setting.SettingName.replace('CapacityHeatmap:', '');
        settingsMap[settingKey] = setting.SettingValue || null;
      });
    }

    return {
      SummaryProcessList: settingsMap.SummaryProcessList || null,
      ExportAttributeCode: settingsMap.ExportAttributeCode || null,
      ExportAttributeValue: settingsMap.ExportAttributeValue || null,
    };
  },

  /**
   * Calculate hours of a work order that fall on a specific calendar date
   * Prorates multi-day WO allocations across all dates they span
   * @param {Date} woStart - Work order start datetime
   * @param {Date} woEnd - Work order end datetime
   * @param {string} dateStr - Target date in YYYY-MM-DD format
   * @returns {number} Hours on this date only, unrounded (full precision)
   */
  calculateWOHoursForDate(woStart, woEnd, dateStr) {
    const dayStart = new Date(dateStr + 'T00:00:00Z');
    const dayEnd = new Date(dateStr + 'T23:59:59Z');
    const nextMidnight = new Date(dayEnd.getTime() + 1000);

    const overlapStart = new Date(Math.max(woStart.getTime(), dayStart.getTime()));
    const overlapEnd = new Date(Math.min(woEnd.getTime(), nextMidnight.getTime()));

    if (overlapStart >= overlapEnd) {
      return 0;
    }

    const milliseconds = overlapEnd - overlapStart;
    const hours = milliseconds / (1000 * 60 * 60);

    return hours;
  },

  /**
   * Build utilisation grid from capacity and demand data
   * Aggregates hours by process and time column, calculates utilisation percentage
   * Work orders spanning multiple days are prorated across all dates they touch
   * @param {Array} capacityList - Raw capacity data from getCapacityData
   * @param {Array} demandList - Raw demand data from getDemandData
   * @param {Array} processes - Process definitions with { ProcessCode, Description }
   * @param {Array} columns - Column definitions with { key, label, subLabel, dates[] }
   * @returns {Object} Nested grid: { [processCode]: { [colKey]: { pct, capacityHours, demandHours, workOrders } | null } }
   */
  buildUtilisationGrid(capacityList, demandList, processes, columns) {
    // Step 1: Build capacity map
    // ShiftDateStart: "2025-10-15T00:00:00Z" — strip Z before substring
    const capacityMap = {};
    capacityList.forEach(item => {
      const dateStr = item.ShiftDateStart.replace('Z', '').substring(0, 10); // → "2025-10-15"
      if (!capacityMap[item.ProcessCode]) {
        capacityMap[item.ProcessCode] = {};
      }
      // StandardDurationHours (full shift including downtime) ✅. Fallback to WorkHours defensively.
      const hours = item.StandardDurationHours != null ? item.StandardDurationHours : (item.WorkHours || 0);
      capacityMap[item.ProcessCode][dateStr] =
        (capacityMap[item.ProcessCode][dateStr] || 0) + hours;
    });

    // Step 2: Build demand map — Prorate multi-day WOs across all dates they span
    // PlannedHours does not exist — derive from EndDate - StartDate.
    // For each date a WO spans, store only the portion of hours for that date
    // ExportQty and OtherQty (in kg) are prorated using the same fraction as hours
    const demandMap = {};
    demandList.forEach(item => {
      if (!item.StartDate || !item.EndDate) {
        return; // Skip if no time range
      }

      // Include Production and Maintenance work orders — exclude Down Time and other non-work types
      if (item.WOType !== 'Production' && item.WOType !== 'Maintenance') {
        return;
      }

      const start = new Date(item.StartDate);  // Keep 'Z' for UTC parsing
      const end = new Date(item.EndDate);      // Keep 'Z' for UTC parsing
      const isMaintenanceOrder = item.WOType === 'Maintenance';

      if (!demandMap[item.ProcessCode]) {
        demandMap[item.ProcessCode] = {};
      }

      // Calculate total hours for this WO across all dates (for proration fraction)
      const totalWOHours = (end - start) / (1000 * 60 * 60);

      // Loop through each calendar date this WO spans
      // Extract YYYY-MM-DD from UTC timestamps (not local timezone)
      const startDateStr = start.toISOString().split('T')[0];
      const endDateStr = end.toISOString().split('T')[0];
      let currentDate = new Date(startDateStr + 'T00:00:00Z');
      const endDateOnly = new Date(endDateStr + 'T00:00:00Z');

      while (currentDate <= endDateOnly) {
        const dateStr = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD

        // Calculate PRORATED hours for this specific date only
        const hoursForDate = this.calculateWOHoursForDate(start, end, dateStr);

        if (hoursForDate > 0) {
          if (!demandMap[item.ProcessCode][dateStr]) {
            demandMap[item.ProcessCode][dateStr] = {
              totalHours: 0,
              exportTonnes: 0,
              otherTonnes: 0,
              maintHours: 0,
              workOrders: [],
            };
          }

          demandMap[item.ProcessCode][dateStr].totalHours += hoursForDate;

          // Prorate quantities using fraction = hoursForDate / totalWOHours
          const fraction = totalWOHours > 0 ? hoursForDate / totalWOHours : 0;
          const exportQtyForDate = (item.ExportQty || 0) * fraction;
          const otherQtyForDate = (item.OtherQty || 0) * fraction;

          // Convert kg to tonnes (÷ 1,000) and round to 1 decimal
          const exportTonnesForDate = Math.round((exportQtyForDate / 1000) * 10) / 10;
          const otherTonnesForDate = Math.round((otherQtyForDate / 1000) * 10) / 10;

          demandMap[item.ProcessCode][dateStr].exportTonnes += exportTonnesForDate;
          demandMap[item.ProcessCode][dateStr].otherTonnes += otherTonnesForDate;

          // Accumulate maintenance hours (only for maintenance orders)
          if (isMaintenanceOrder) {
            demandMap[item.ProcessCode][dateStr].maintHours += hoursForDate;
          }

          demandMap[item.ProcessCode][dateStr].workOrders.push({
            workOrderCode: item.WorkOrderCode,
            productDescription: item.ProductDescription1 || '',
            hours: hoursForDate,
            isMaintenanceOrder,
            exportTonnes: exportTonnesForDate,
            otherTonnes: otherTonnesForDate,
            totalTonnes: exportTonnesForDate + otherTonnesForDate,
          });
        }

        // Move to next calendar day (UTC-aware increment)
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
      }
    });

    // Step 3: For each process × column, aggregate and compute utilisation %
    const grid = {};
    processes.forEach(proc => {
      grid[proc.ProcessCode] = {};
      columns.forEach(col => {
        let capacityHours = 0;
        let demandHours = 0;
        let exportTonnes = 0;
        let otherTonnes = 0;
        let maintHours = 0;
        let workOrders = [];

        col.dates.forEach(dateStr => {
          if (capacityMap[proc.ProcessCode] && capacityMap[proc.ProcessCode][dateStr]) {
            capacityHours += capacityMap[proc.ProcessCode][dateStr];
          }
          if (demandMap[proc.ProcessCode] && demandMap[proc.ProcessCode][dateStr]) {
            const dateData = demandMap[proc.ProcessCode][dateStr];
            demandHours += dateData.totalHours;
            exportTonnes += dateData.exportTonnes;
            otherTonnes += dateData.otherTonnes;
            maintHours += dateData.maintHours;
            workOrders = workOrders.concat(dateData.workOrders);
          }
        });

        capacityHours = Math.round(capacityHours * 100) / 100;
        demandHours = Math.round(demandHours * 100) / 100;
        exportTonnes = Math.round(exportTonnes * 10) / 10;
        otherTonnes = Math.round(otherTonnes * 10) / 10;
        maintHours = Math.round(maintHours * 100) / 100;

        if (capacityHours === 0) {
          // No shift assigned — no-data cell
          grid[proc.ProcessCode][col.key] = null;
        } else {
          const pct = Math.round((demandHours / capacityHours) * 100);
          const totalTonnes = Math.round((exportTonnes + otherTonnes) * 10) / 10;
          workOrders.sort((a, b) => b.hours - a.hours);
          grid[proc.ProcessCode][col.key] = {
            pct,
            capacityHours,
            demandHours,
            exportTonnes,
            otherTonnes,
            totalTonnes,
            maintHours,
            workOrders,
          };
        }
      });
    });

    return grid;
  },
};
